<template>
  <div class="b-booking-aside">
    <div class="b-booking-aside__top-section mb-16">
      <div class="b-booking-aside__header">
        <span class="b-booking-aside__title h2">
          {{ dictionary.asideHeaderTitle }}
        </span>

        <Anchor
          v-if="editVehicleLink"
          :item="editVehicleLink"
          :hide-text="true"
          class="btn btn-link-light btn-icon-start btn-edit">
          <i class="icon-edit"></i>
          <small>{{ editVehicleLink.text }}</small>
        </Anchor>
      </div>
      <div ref="thresholdElement" class="b-booking-aside__vehicle">
        <div class="b-vehicle view-list">
          <div class="b-vehicle__tagged">
            <VehicleTag :item="item" />
          </div>

          <div class="b-vehicle__body">
            <div class="b-vehicle__header mb-40">
              <div class="b-vehicle__title h4 extrabold">
                {{ item.Model }}
              </div>
              <div class="b-vehicle__groups mb-10 semibold">
                <ul>
                  <li data-bs-toggle="tooltip" :title="item.AcrissData">
                    {{ item.GroupAcrissTxt }}
                    <span>
                      <LazyImage
                        :image="{
                          url: '/imgs/icons/tooltip.svg',
                          alt: 'Tooltip',
                        }" />
                    </span>
                  </li>
                  <li class="separator"></li>
                  <li>{{ item.CategoryNames }}</li>
                </ul>
              </div>
              <div class="b-vehicle__details">
                <ul>
                  <li>
                    <div class="pair bold">
                      <i class="icon-passenger"></i>
                      {{ item.PassengersTxt }}
                    </div>
                  </li>

                  <li>
                    <div class="b-vehicle__luggages">
                      <div
                        v-if="item.BigLuggage"
                        class="pair bold"
                        data-bs-toggle="tooltip"
                        :title="item.BigLuggageTxt">
                        <i class="icon-suitcase-large"></i>
                        <span>x{{ item.BigLuggage }}</span>
                      </div>

                      <div
                        v-if="item.SmallLuggage"
                        class="pair bold"
                        data-bs-toggle="tooltip"
                        :title="item.SmallLuggageTxt">
                        <i class="icon-suitcase"></i>
                        <span>x{{ item.SmallLuggage }}</span>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div class="pair bold">
                      <i class="icon-ac"></i>
                      <span v-if="item.HasAC">{{ dictionary.acYes }}</span>
                      <span v-else>{{ dictionary.acNo }}</span>
                    </div>
                  </li>
                  <li v-if="showTransmission">
                    <div class="pair bold">
                      <i class="icon-transmission"></i>
                      <span v-if="item.IsManual">
                        {{ dictionary.transmissionManual }}
                      </span>
                      <span v-if="item.IsAutomatic">
                        {{ dictionary.transmissionAutomatic }}
                      </span>
                    </div>
                  </li>
                  </ul>
                  <ul v-if="item.Extras" class="columns-1">
                    <li>
                      <div class="pair bold">
                        <i class="icon-info"></i>
                        <span v-html="item.Extras"></span>
                      </div>
                    </li>
                  </ul>

              </div>
            </div>
            <div
              class="b-vehicle__picture"
              :class="{
                'mb-12': milesNBonusLoggedIn && showMilesNBonus,
                'mb-30': !milesNBonusLoggedIn && !showMilesNBonus,
              }">
              <LazyImage v-if="image" :image="image" />
            </div>

            <div
              v-if="showMilesAccrual"
              class="miles-accrual-banner miles-accrual-banner--aside">
              <div class="miles-accrual-banner__cont">
                <div class="d-flex" style="gap: 8px">
                  <LazyImage
                    v-if="milesNBonusLogoCompact"
                    :image="milesNBonusLogoCompact" />
                  <div
                    class="miles-accrual-banner__tooltip bold"
                    data-bs-toggle="tooltip"
                    :title="mbDictionary.milesNBonusAccrualTooltip">
                    <span>
                      {{ mbDictionary.milesNBonusBannerAsidePrefix }}
                    </span>
                    <LazyImage
                      :image="{
                        url: '/imgs/icons/tooltip.svg',
                        alt: 'Tooltip',
                      }" />
                  </div>
                </div>
                <span class="bold">
                  {{ item.MilesAccrual.toLocaleString(lang) }}
                  {{ mbDictionary.milesNBonusBannerAsideSufix }}
                </span>
              </div>
            </div>

            <div class="b-vehicle__extras mb-20">
              <p>
                <small>{{ dictionary.bookingIncludes }}</small>
              </p>
              <ul>
                <li v-for="(extra, index) in item.ExtrasList" :key="index">
                  {{ extra }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div ref="stickyElement" class="b-booking-aside__main">
      <div class="b-booking-aside__extras mb-20 p">
        <div class="b-booking-aside__extras__header mb-20">
          <label for="" class="form-label extrabold mb-0">
            {{ dictionary.asideExtrasLabel }}
          </label>

          <Anchor
            v-if="editExtrasLink"
            :item="editExtrasLink"
            :hide-text="true"
            class="btn btn-link btn-icon-start btn-edit">
            <i class="icon-edit"></i>
            <small>{{ editExtrasLink.text }}</small>
          </Anchor>
        </div>

        <!-- prepaid -->
        <ul v-if="payment?.IsPrepaid && payment?.PrepaidAmenities.length">
          <li v-for="(option, index) in payment.PrepaidAmenities" :key="index">
            <span>
              {{ option.Description }}
              <span v-if="option.Quantity > 1">(x{{ option.Quantity }})</span>
            </span>
            <em class="h4 extrabold" v-html="option.Cost"></em>
          </li>
        </ul>
        <!-- non-prepaid -->
        <ul v-else-if="!payment?.IsPrepaid && payment?.ReturnAmenities.length">
          <li v-for="(option, index) in payment.ReturnAmenities" :key="index">
            <span>
              {{ option.Description }}
              <span v-if="option.Quantity > 1">(x{{ option.Quantity }})</span>
            </span>
            <em class="h4 extrabold" v-html="option.Cost"></em>
          </li>
        </ul>
        <p v-else>{{ dictionary.asideExtrasNoResults }}</p>
      </div>

      <div
        v-if="!payment?.IsPrepaid && payment?.ReturnAmenities.length"
        class="b-booking-aside__cost mb-20">
        <span>{{ dictionary.asideReturnAmenitiesCost }}</span>
        <em class="h4 extrabold" v-html="payment?.ReturnAmenitiesCost"></em>
      </div>

      <div v-if="showCostOfVehicle" class="b-booking-aside__cost mb-20">
        <span>{{ dictionary.asideCostOfVehicle }}</span>
        <em class="h4 extrabold" v-html="payment?.VehicleCost"></em>
      </div>

      <BookingAsideDefault
        v-if="currentPage() === 'home'"
        :payment="payment"
        :redeem-miles-enabled="redeemMilesEnabled"
        :miles-data="milesData" />

      <BookingAsideModifyConfirm
        v-if="currentPage() === 'modify'"
        :payment="payment"
        :redeem-miles-enabled="redeemMilesEnabled"
        :has-discount="hasDiscount"
        :miles-data="milesData" />

      <BookingAsideModifyUp
        v-if="currentPage() === 'modify-up'"
        :payment="payment"
        :has-discount="hasDiscount"
        :redeem-miles-enabled="redeemMilesEnabled"
        :miles-data="milesData"
        :price-difference="priceDifference" />

      <BookingAsideModifyDown
        v-if="currentPage() === 'modify-down'"
        :payment="payment"
        :has-discount="hasDiscount"
        :miles-data="milesData" />

      <div v-if="showSubmitButton" class="b-booking-aside__footer">
        <button
          type="button"
          class="btn btn-primary btn-full-width"
          @click="onClickSubmit">
          {{ buttonSubmitText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import * as lib from '../../lib/lib'
import LazyImage from './common/LazyImage.vue'
import VehicleTag from './VehicleTag.vue'
import Anchor from './../components/common/Anchor.vue'
import BookingAsideDefault from './BookingAsideStates/BookingAsideDefault.vue'
import BookingAsideModifyConfirm from './BookingAsideStates/BookingAsideModifyConfirm.vue'
import BookingAsideModifyUp from './BookingAsideStates/BookingAsideModifyUp.vue'
import BookingAsideModifyDown from './BookingAsideStates/BookingAsideModifyDown.vue'
import AsideStickyOnScroll from '../../modules/AsideStickyOnScroll'

export default {
  components: {
    BookingAsideModifyConfirm,
    BookingAsideDefault,
    BookingAsideModifyDown,
    BookingAsideModifyUp,
    LazyImage,
    Anchor,
    VehicleTag,
  },
  props: {
    item: {
      required: true,
      type: Object,
    },
    payment: {
      required: true,
      type: Object,
    },
    showSubmitButton: {
      required: false,
      type: Boolean,
    },
    step: {
      required: false,
      type: Number,
      default: 0,
    },
    redeemMilesEnabled: {
      required: false,
      type: Boolean,
    },
    milesData: {
      required: false,
      type: Object,
      default: null,
    },
  },
  emits: ['submit'],
  data() {
    return {
      editVehicleLink: null,
      editExtrasLink: null,
      prepaidAmenities: [],

      mbDictionary: {},
      milesNBonusLogoCompact: null,
      milesNBonusLoggedIn: false,
      showMilesNBonus: lib.getData('milesNBonusData'),
      destinationAllowed: true,
      nodeTypeAlias: '',
      rate: 1,
      lang: 'en',

      formatOptions: {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },

      priceDifference: 0,
    }
  },
  computed: {
    showMilesAccrual() {
      if (
        this.nodeTypeAlias.toLowerCase().includes('modify') &&
        this.item.MilesAccrual
      ) {
        return true
      }

      return (
        this.showMilesNBonus &&
        this.milesNBonusLoggedIn &&
        this.item.MilesAccrual
      )
    },

    hasDiscount() {
      return this.payment.RedeemDiscountDecimal > 0
    },

    buttonSubmitText() {
      return this.isOnRequest && this.step === 3
        ? this.dictionary.buttonSubmitTextOnRequest
        : this.dictionary.buttonSubmitText
    },

    isOnRequest() {
      return this.item?.IsRequest
    },

    showCostOfVehicle() {
      // #7384920 Στο prepaid εάν ο χρήστης δεν έχει επιλέξει extra, να μην εμφανίζεται το «Επιλεγμένο όχημα», παρα μόνο το «Συνολικό Κόστος»
      if (
        this.payment?.IsPrepaid &&
        !this.payment?.PrepaidAmenities.length &&
        !this.payment?.ReturnAmenities.length
      ) {
        return false
      }
      return true
    },

    showTransmission() {
      return this.item.IsManual || this.item.IsAutomatic
    },

    image() {
      const images = this.item.Images
      if (!images.length) {
        return null
      }
      return {
        url: images[0],
        alt: '',
      }
    },
  },
  mounted() {
    const {
      editVehicleLink,
      editExtrasLink,
      priceDifference,
      configuration,
      preModifyTotalCostDecimal,
      preModifyRedeemDiscountDecimal,
    } = lib.getData('bookingData')
    this.editVehicleLink = editVehicleLink
    this.editExtrasLink = editExtrasLink
    this.priceDifference = priceDifference
    this.preModifyTotalCostDecimal = preModifyTotalCostDecimal
    this.preModifyRedeemDiscountDecimal = preModifyRedeemDiscountDecimal

    if (configuration && configuration.nodeTypeAlias)
      this.nodeTypeAlias = configuration.nodeTypeAlias

    this.lang = lib.getLocaleFromLangAttr()
    if (lib.getData('milesNBonusData')) {
      const { dictionary, configuration, milesNBonusLogoCompact } =
        lib.getData('milesNBonusData')
      this.mbDictionary = dictionary
      this.milesNBonusLoggedIn = configuration.milesNBonusLoggedIn
      this.rate = configuration.rate
      this.milesNBonusLogoCompact = milesNBonusLogoCompact
      this.destinationAllowed = configuration.destinationAllowed

      if (!this.destinationAllowed) {
        this.showMilesNBonus = false
      }

      this.setEventListeners()
    }

    this.$nextTick(() => {
      lib.iniTooltips(document)
      this.AsideStickyOnScroll = new AsideStickyOnScroll(
        this.$refs.stickyElement,
        this.$refs.thresholdElement
      )
    })
  },
  methods: {
    setEventListeners() {
      document.addEventListener('milesNBonusLogStatus', (e) => {
        this.milesNBonusLoggedIn = e.detail.logStatus
      })
      document.addEventListener('updatedMBValues', () => {
        this.$nextTick(() => {
          lib.iniTooltips(document)
        })
      })
    },

    currentPage() {
      const page = this.nodeTypeAlias.toLowerCase()

      if (page.includes('modify')) {
        if (this.priceDifference > 0) {
          return 'modify-up'
        } else if (this.priceDifference < 0) {
          return 'modify-down'
        }
        return 'modify'
      }

      return 'home'
    },

    onClickSubmit() {
      this.$emit('submit')
    },
  },
}
</script>
